/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { I18nContext } from '@bingads-webui-react/i18n-context';
import classnames from 'classnames';
import { Link, Label, Stack } from '@bingads-webui-react/fabric-control';
import PropTypes from 'prop-types';
import React, { useMemo, useContext, useState, useCallback, useEffect } from 'react';
import _ from 'underscore';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { AdSchedule,
  BaseExclusion, UrlOptions, AudienceAds, usePatch,
  getUrlOptionsDetails, getExclusionsDetails, getAudienceAdsDetails,
  globalErrorMsg, isAudienceAdsValid } from '@bingads-webui-campaign/hotel-common-upsert';
import { AdDistribution } from '@bingads-webui-campaign/ad-distribution';
import { checkShouldShowError, getAdScheduleDetails } from '@bingads-webui-campaign-react/wizard-shared-components';
import { Collapse, TextTitle } from '@bingads-webui-campaign/wizard-jumbo-card';
import { useCurrentCampaign, useCurrentAdGroup } from '@bingads-webui-campaign-react/use-current-scope-entity';
import { scenarios } from '../shared';

const getStyles = () =>
  ({
    advancedSettingsSection: {
      marginTop: '36px',
      '& .displayNone': {
        display: 'none',
      },
      '& .expandedImage:before, .expanderImage:before': {
        marginRight: '12px',
      },
    },
    advancedSettingsContainer: {
      marginLeft: '32px',
    },
  });

function getAdDistributionOption() {
  return {
    AdDistribution: 'BingYahooAndSearchPartners',
    campaignType: 'hotel',
    Languages: ['All'],
  };
}

function getAdScheduleOption(campaign, isCreation) {
  const model = {
    dayTimeRanges: campaign.dayTimeRanges,
    timeZone: campaign.TimeZone,
    adScheduleUseSearcherTimeZone: campaign.AdScheduleUseSearcherTimeZone,
  };
  return {
    model,
    level: 'campaign',
    showDateSettings: isCreation,
    hideAdScheduleBidAdjustments: campaign.bidStrategy.option === 'Commission',
  };
}

export const AdvancedSettings = (props) => {
  const {
    campaign,
    scenario,
    errMsg,
    patchErrMsg,
    patchCampaign,
    getAdScheduleView,
    setAdScheduleView,
    setAdDistributionView,
    getAdDistributionView,
    setUrlOptionsView,
    setSiteExclusionView,
    setIPExclusionView,
    getUrlOptionsValues,
    getExclusionsValues,
    advancedSettinsApiErrors,
    isDataFetched,
  } = props;

  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getStyles);
  const currentCampaign = useCurrentCampaign();
  const currentAdgroup = useCurrentAdGroup();
  const campaignId = currentCampaign ? currentCampaign.Id : null;
  const adgroupId = currentAdgroup ? currentAdgroup.Id : null;
  const setAudienceAds = usePatch(patchCampaign, 'audienceAds');
  const isCreation = scenario === scenarios.creation;

  const allPanelItems = _.compact(['adSchedule', 'audienceAds', isCreation && 'adDistribution', 'urlOptions', 'baseExclusion']);
  const [activeKeys, setActiveKeys] = useState(['adSchedule', 'audienceAds', 'adDistribution']);

  useEffect(() => {
    const hasApiErrorKeys = Object.keys(advancedSettinsApiErrors);
    const needToExpandKeys = [];
    allPanelItems.forEach((key) => {
      if (hasApiErrorKeys.includes(key) && advancedSettinsApiErrors[key] && advancedSettinsApiErrors[key].length) {
        patchErrMsg(key, globalErrorMsg);
        needToExpandKeys.push(key);
      }
    });
    setActiveKeys(activeKeys.concat(needToExpandKeys));
  }, [advancedSettinsApiErrors]);

  const onCollapseAll = () => {
    if (activeKeys.length) {
      setActiveKeys([]);
    } else {
      setActiveKeys(allPanelItems);
    }
  };

  // After collecting the needed data, need to ensure the view is created once.
  const adScheduleOption = useMemo(
    () => getAdScheduleOption(campaign, isCreation),
    [campaign.dayTimeRanges, campaign.AdScheduleUseSearcherTimeZone, isCreation]
  );

  const adDistributionOption = useMemo(
    () => getAdDistributionOption(),
    []
  );


  const setUrlOptionsActiveCallback = useCallback(() => { patchCampaign('urlOptions', getUrlOptionsValues()); }, []);
  const setExclusionsActiveCallback = useCallback(() => { patchCampaign('exclusions', getExclusionsValues()); }, []);

  return (
    <div className={classnames('advanced-settings-section', classes.advancedSettingsSection)}>
      <Stack horizontal horizontalAlign="space-between" tokens={{ padding: '0 0 12px 0' }}>
        <Label>{i18n.getString(_TL_('ADVANCED SETTINGS'))}</Label>
        <Link className="jumbo-card-control-all-btn" onClick={onCollapseAll}>{activeKeys.length ? i18n.getString(_TL_('Collapse all')) : i18n.getString(_TL_('Expand all'))}</Link>
      </Stack>
      <Collapse allPanelItems={allPanelItems} checkItems activeKeys={activeKeys} setActiveKeys={setActiveKeys}>
        {isDataFetched &&
          <Collapse.Panel
            key="adSchedule"
            title={<TextTitle
              titleKey={_TL_('Ad schedule')}
              topicKey="pop_BA_Targeting_DayTime"
              details={getAdScheduleDetails(i18n, getAdScheduleView())}
            />}
            error={errMsg.adSchedule}
          >
            <AdSchedule
              key="ad-schedule"
              setAdScheduleView={setAdScheduleView}
              options={adScheduleOption}
              getAdScheduleView={getAdScheduleView}
              getCreatedView={getAdScheduleView}
              managed
            />
          </Collapse.Panel>}
        <Collapse.Panel
          key="audienceAds"
          title={<TextTitle
            titleKey={_TL_('Audience ads')}
            details={getAudienceAdsDetails({ rawData: campaign.audienceAds, i18n })}
          />}
          error={!isAudienceAdsValid(campaign.audienceAds) && checkShouldShowError(campaign.audienceAds) ? globalErrorMsg : ''}
        >
          <AudienceAds key="audience-ads" setAudienceAds={setAudienceAds} audienceAds={campaign.audienceAds} />
        </Collapse.Panel>
        {isCreation &&
          <Collapse.Panel
            key="adDistribution"
            title={<TextTitle
              titleKey={_TL_('Ad distribution')}
              topicKey="pop_BA_NewCampaignCreate_AdDistribution"
              details={(() => {
                const view = getAdDistributionView();
                return view ? view.getTranslatedOption() : '';
              })()}
            />}
            error={errMsg.adDistribution}
          >
            <AdDistribution key="ad-distribution" setAdDistributionView={setAdDistributionView} options={adDistributionOption} />
          </Collapse.Panel>}
        {!isCreation &&
          <Collapse.Panel key="urlOptions" title={<TextTitle titleKey={_TL_('URL options')} details={getUrlOptionsDetails(i18n, campaign.urlOptions)} />} error={errMsg.urlOptions} setActiveCallback={setUrlOptionsActiveCallback}>
            <UrlOptions
              key="url-options"
              setUrlOptionsView={setUrlOptionsView}
              options={{
                level: 'campaign', campaignId, adgroupId, model: campaign.urlOptions,
              }}
            />
          </Collapse.Panel>}
        {
          scenario === 'settings' &&
          <Collapse.Panel key="baseExclusion" title={<TextTitle titleKey={_TL_('Website & IP address exclusions')} details={getExclusionsDetails(i18n, campaign.exclusions)} />} error={errMsg.baseExclusion} setActiveCallback={setExclusionsActiveCallback}>
            <BaseExclusion
              key="base-exclusion"
              patchData={patchCampaign}
              setSiteExclusionView={setSiteExclusionView}
              setIPExclusionView={setIPExclusionView}
              options={{
              level: 'campaign', campaignId, adgroupId, showSiteExclusion: true, showIPExclusion: true,
            }}
            />
          </Collapse.Panel>
         }
      </Collapse>
    </div>
  );
};

AdvancedSettings.propTypes = {
  campaign: PropTypes.shape({}).isRequired,
  scenario: PropTypes.string.isRequired,
  errMsg: PropTypes.shape({}),
  patchErrMsg: PropTypes.func.isRequired,
  patchCampaign: PropTypes.func.isRequired,
  setAdScheduleView: PropTypes.func.isRequired,
  getAdScheduleView: PropTypes.func.isRequired,
  setAdDistributionView: PropTypes.func.isRequired,
  getAdDistributionView: PropTypes.func.isRequired,
  setUrlOptionsView: PropTypes.func.isRequired,
  setSiteExclusionView: PropTypes.func.isRequired,
  setIPExclusionView: PropTypes.func.isRequired,
  getUrlOptionsValues: PropTypes.func.isRequired,
  getExclusionsValues: PropTypes.func.isRequired,
  advancedSettinsApiErrors: PropTypes.shape({}),
  isDataFetched: PropTypes.bool.isRequired,
};
AdvancedSettings.defaultProps = {
  errMsg: {},
  advancedSettinsApiErrors: {},
};
